import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Menu, MenuItem, ScrollableBlock } from 'carbon-react/lib/components/menu';
import Box from 'carbon-react/lib/components/box';
import { useNavigate, useLocation } from 'react-router-dom';
import { IntlShape, useIntl } from 'react-intl';

import {
  checkPath,
  getPredictSettingsAccountPageUrl,
  getPredictSettingsOrganisationsPageUrl,
  getPredictSettingsOrganisationsPath,
  getPredictSettingsUsersPageUrl,
  getPredictSettingsUsersPath
} from '@futrli/shared-services';
import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';

interface Props {
  workspaces: Workspace[] | WorkspacesListingItem[];
  selected?: Workspace | WorkspacesListingItem;
}

const getWorkspaceDisplayText = (
  isDefaultWorkspaceEnabled: boolean,
  intl: IntlShape,
  workspace?: Workspace | WorkspacesListingItem
) => {
  if (workspace) {
    const workspaceName =
      workspace.name ||
      intl.formatMessage(
        { id: 'landing.default_workspace_name', defaultMessage: "{name}'s workspace" },
        { name: workspace?.owner.first_name }
      );
    if ((workspace as WorkspacesListingItem)?.member?.is_default_workspace && isDefaultWorkspaceEnabled) {
      return intl.formatMessage(
        { id: 'common.default_workspace', defaultMessage: '{workspace} (Default)' },
        { workspace: workspaceName }
      );
    }
    return workspaceName;
  }

  return '';
};

interface MenuItemContentProps {
  workspaces: Workspace[] | WorkspacesListingItem[];
  handleMenuItemClick: (item: Workspace | WorkspacesListingItem) => void;
  isDefaultWorkspaceEnabled: boolean;
}

const MenuItemContent = ({ workspaces, handleMenuItemClick, isDefaultWorkspaceEnabled }: MenuItemContentProps) => {
  const intl = useIntl();

  const workspaceLengthWithoutScroll = 12; // Height of each item is 40px

  if (workspaces.length > workspaceLengthWithoutScroll) {
    return (
      <ScrollableBlock height={480}>
        {(workspaces as WorkspacesListingItem[]).map(w => (
          <MenuItem key={w.id} onClick={() => handleMenuItemClick(w)}>
            {getWorkspaceDisplayText(isDefaultWorkspaceEnabled, intl, w)}
          </MenuItem>
        ))}
      </ScrollableBlock>
    );
  }

  return (
    <>
      {(workspaces as WorkspacesListingItem[]).map(w => (
        <MenuItem key={w.id} onClick={() => handleMenuItemClick(w)}>
          {getWorkspaceDisplayText(isDefaultWorkspaceEnabled, intl, w)}
        </MenuItem>
      ))}
    </>
  );
};

export const WorkspaceDropdown = ({ workspaces, selected }: Props) => {
  const [menuKey, setMenuKey] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const intl = useIntl();

  const flags = useFlags();

  const shouldShowDropdown = workspaces.length > 1;

  const handleMenuItemClick = (item: Workspace | WorkspacesListingItem) => {
    setMenuKey(prev => prev + 1); // force remount to close dropdown
    const isOrganisationSettings = checkPath(location.pathname, getPredictSettingsOrganisationsPath());
    const isUsersSettings = checkPath(location.pathname, getPredictSettingsUsersPath());

    const pushRoute = isOrganisationSettings
      ? getPredictSettingsOrganisationsPageUrl(item.id)
      : isUsersSettings
      ? getPredictSettingsUsersPageUrl(item.id)
      : getPredictSettingsAccountPageUrl(item.id);
    navigate(pushRoute);
  };

  return (
    <Box ml="-16px">
      <Menu menuType="black" key={menuKey}>
        <MenuItem
          submenu={getWorkspaceDisplayText(!!flags.defaultWorkspace, intl, selected)}
          showDropdownArrow={shouldShowDropdown}
          maxWidth="350px"
        >
          <MenuItemContent
            workspaces={workspaces}
            handleMenuItemClick={handleMenuItemClick}
            isDefaultWorkspaceEnabled={!!flags.defaultWorkspace}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};
